import React, { useState, useEffect } from 'react';
import ReactConfetti from 'react-confetti';
import { toolClient } from '../../../../services/toolClient';

interface PracticeExercisesProps {
  conversationText: string;
  onClose: () => void;
  mcqPrompt: string;
  renderBackButton: () => JSX.Element;
  abilities?: string[];
  skills?: string[];
}

interface MCQOption {
  option_id: string;
  answer_text: string;
  explanation: string;
  is_correct: boolean;
}

interface MCQ {
  metadata: {
    question_id: string;
    ability: string;
    level: string;
    skills: Array<{ skill: string }>;
  };
  content: {
    question_text: string;
    options: MCQOption[];
  };
}

export const PracticeExercises: React.FC<PracticeExercisesProps> = ({
  conversationText,
  onClose,
  mcqPrompt,
  renderBackButton,
  abilities,
  skills
}) => {
  const [exercises, setExercises] = useState<MCQ[]>([]);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(-1);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<{ correct: boolean; selectedOption: string }[]>([]);
  const [showSummary, setShowSummary] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });
  const [hasGeneratedExercises, setHasGeneratedExercises] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Generate exercises when component mounts
  React.useEffect(() => {
    if (!hasGeneratedExercises) {
      generateExercises();
      setHasGeneratedExercises(true);
    }
  }, [conversationText, mcqPrompt, hasGeneratedExercises]);

  const generateExercises = async () => {
    try {
      setIsLoading(true);
      setError(null);

      console.log('Generating exercises with conversation text:', conversationText);

      // Build the system prompt with ability and skill information if provided
      let systemPrompt = mcqPrompt;
      if (abilities?.length) {
        systemPrompt += `\nFocus on testing these abilities: ${abilities.join(', ')}`;
      }
      if (skills?.length) {
        systemPrompt += `\nInclude these specific skills: ${skills.join(', ')}`;
      }

      const toolResp = await toolClient.executeAsyncAndWait<{ questions: MCQ[] }>(
        'create_exercise_card',
        '1.0.0',
        {
          conversation_text: conversationText,
          mcq_prompt: systemPrompt,
          abilities,
          skills
        },
        {
          client: 'web',
          appVersion: process.env.REACT_APP_VERSION || 'dev'
        },
        { intervalMs: 1500, timeoutMs: 10 * 60 * 1000 }
      );
      if (!toolResp.ok || !toolResp.result?.questions) {
        throw new Error(toolResp?.error?.message || 'Failed to generate exercises');
      }
      const response: any = { content: { questions: toolResp.result.questions } };

      console.log('Raw response from promptService:', response);
      console.log('Response content type:', typeof response.content);
      
      if (response.content && typeof response.content === 'object') {
        console.log('Response content structure:', JSON.stringify(response.content, null, 2));
        
        if ('questions' in response.content) {
          console.log('Number of questions received:', response.content.questions.length);
          console.log('First question structure:', JSON.stringify(response.content.questions[0], null, 2));
          
          // Batch state updates together
          const questions = response.content.questions;
          React.startTransition(() => {
            setExercises(questions);
            if (questions.length > 0) {
              setCurrentExerciseIndex(0);
            }
          });
        } else {
          console.error('Response content does not contain questions array:', response.content);
          throw new Error('Invalid response format: missing questions array');
        }
      } else {
        console.error('Response content is not an object:', response.content);
        throw new Error('No exercises generated');
      }
    } catch (err) {
      console.error('Error in generateExercises:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate exercises');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAnswerSelect = (optionId: string) => {
    if (showFeedback) return;
    console.log('Selected answer:', optionId);
    console.log('Current exercise:', exercises[currentExerciseIndex]);
    
    setSelectedAnswer(optionId);
    setShowFeedback(true);

    const currentExercise = exercises[currentExerciseIndex];
    const selectedOption = currentExercise.content.options.find(opt => opt.option_id === optionId);
    console.log('Selected option:', selectedOption);
    
    const isCorrect = selectedOption?.is_correct || false;
    console.log('Answer is correct:', isCorrect);

    setAnswers([...answers, { correct: isCorrect, selectedOption: optionId }]);
  };

  const handleNext = () => {
    if (currentExerciseIndex === exercises.length - 1) {
      setShowSummary(true);
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 5000);
    } else {
      setCurrentExerciseIndex(currentExerciseIndex + 1);
      setSelectedAnswer(null);
      setShowFeedback(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-talaera-blue"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="text-red-600 mb-4">{error}</div>
        <button
          onClick={onClose}
          className="px-4 py-2 bg-talaera-blue text-white rounded hover:bg-talaera-blue-dark transition-colors"
        >
          Close
        </button>
      </div>
    );
  }

  if (showSummary) {
    const correctAnswers = answers.filter(a => a.correct).length;
    const totalQuestions = exercises.length;
    const percentage = (correctAnswers / totalQuestions) * 100;

    // Determine the motivational message based on score
    const getMotivationalMessage = (percentage: number) => {
      if (percentage >= 80) {
        return "Excellent work! You're really getting the hang of it!";
      } else if (percentage >= 60) {
        return "Good progress! Keep practicing to master these skills.";
      } else {
        return "Keep working on it! These are challenging concepts that take time to master.";
      }
    };

    // Extract skills from the exercises (using the first exercise as reference)
    const skillArea = exercises[0]?.metadata?.skills?.[0]?.skill || "business communication";

    // Generate recommended courses based on the skill area
    const getRecommendedCourses = (skill: string) => {
      switch (skill.toLowerCase()) {
        case "diplomatic communication":
          return [
            {
              title: "Mastering Diplomatic Language in Business",
              description: "Learn proven techniques for handling disagreements professionally",
              duration: "2 hours"
            },
            {
              title: "The Art of Constructive Feedback",
              description: "Transform challenging conversations into opportunities for growth",
              duration: "1.5 hours"
            },
            {
              title: "Professional Disagreement Strategies",
              description: "Express opposing views while maintaining positive relationships",
              duration: "2 hours"
            }
          ];
        case "presentation skills":
          return [
            {
              title: "Executive Presentation Mastery",
              description: "Deliver compelling presentations to senior leadership",
              duration: "2.5 hours"
            },
            {
              title: "Data Storytelling for Business",
              description: "Transform complex information into engaging narratives",
              duration: "2 hours"
            },
            {
              title: "Advanced Q&A Handling",
              description: "Master the art of handling tough questions confidently",
              duration: "1.5 hours"
            }
          ];
        default:
          return [
            {
              title: "Business Communication Excellence",
              description: "Essential strategies for clear and effective business communication",
              duration: "2 hours"
            },
            {
              title: "Professional Email Writing",
              description: "Write clear, concise, and impactful business emails",
              duration: "1.5 hours"
            },
            {
              title: "Cross-Cultural Business Communication",
              description: "Navigate cultural differences in global business contexts",
              duration: "2 hours"
            }
          ];
      }
    };

    const recommendedCourses = getRecommendedCourses(skillArea);

    return (
      <div className="p-8">
        {showConfetti && (
          <ReactConfetti
            width={windowSize.width}
            height={windowSize.height}
            numberOfPieces={200}
            recycle={false}
            colors={['#60A5FA', '#34D399', '#F87171', '#FBBF24', '#A78BFA']}
          />
        )}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-32 h-32 rounded-full bg-gradient-to-r from-talaera-blue to-talaera-blue-dark">
            <span className="text-4xl font-bold text-white">{percentage.toFixed(0)}%</span>
          </div>
          <p className="text-xl mt-6 text-gray-700">
            {getMotivationalMessage(percentage)}
          </p>
        </div>

        <div className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-6">
            Recommended Courses for {skillArea}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {recommendedCourses.map((course, index) => (
              <div 
                key={index}
                className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden border border-gray-100"
              >
                <div className="p-6">
                  <h4 className="text-lg font-semibold text-talaera-blue mb-2">
                    {course.title}
                  </h4>
                  <p className="text-gray-600 text-sm mb-4">
                    {course.description}
                  </p>
                  <div className="flex items-center text-gray-500 text-sm">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {course.duration}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end mt-8">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-talaera-blue text-white rounded hover:bg-talaera-blue-dark transition-colors"
          >
            Back to Feedback
          </button>
        </div>
      </div>
    );
  }

  const currentExercise = exercises[currentExerciseIndex];
  if (!currentExercise) return null;

  const progress = ((currentExerciseIndex + 1) / exercises.length) * 100;

  return (
    <div className="p-8">
      <div className="flex items-center space-x-4 mb-4">
        <div className="flex-shrink-0">
          <button
            onClick={onClose}
            className="p-2 text-gray-600 hover:text-gray-800 transition-colors"
            title="Back to skill"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
        </div>
        <div className="flex-grow h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-talaera-blue transition-all duration-300 ease-out rounded-full"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex-shrink-0 text-sm text-gray-500">
          {currentExerciseIndex + 1}/{exercises.length}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
        <div className="mb-4">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <div className="text-sm text-gray-600">
              Level: {currentExercise.metadata.level}
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                {currentExercise.metadata.ability}
              </span>
              <span className="text-gray-400">|</span>
              <div className="flex flex-wrap gap-2">
                {currentExercise.metadata.skills.map((skill: { skill: string }, index: number) => (
                  <span 
                    key={index}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {skill.skill}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <p className="text-lg mb-6">{currentExercise.content.question_text}</p>
        
        <div className="space-y-4">
          {currentExercise.content.options.map((option) => (
            <button
              key={option.option_id}
              onClick={() => handleAnswerSelect(option.option_id)}
              disabled={showFeedback}
              className={`w-full p-4 text-left border rounded-lg transition-colors ${
                selectedAnswer === option.option_id
                  ? option.is_correct
                    ? 'border-green-500 bg-green-50'
                    : 'border-red-500 bg-red-50'
                  : 'hover:border-talaera-blue hover:shadow-sm'
              } ${showFeedback ? 'cursor-default' : 'cursor-pointer'}`}
            >
              <div className="flex items-center justify-between">
                <span>{option.answer_text}</span>
                {showFeedback && selectedAnswer === option.option_id && (
                  <span>
                    {option.is_correct ? (
                      <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                  </span>
                )}
              </div>
              {showFeedback && selectedAnswer === option.option_id && (
                <p className="mt-3 text-sm text-gray-600">{option.explanation}</p>
              )}
            </button>
          ))}
        </div>
      </div>

      {showFeedback && (
        <div className="flex justify-end">
          <button
            onClick={handleNext}
            className="px-6 py-3 bg-talaera-blue text-white rounded hover:bg-talaera-blue-dark transition-colors"
          >
            {currentExerciseIndex === exercises.length - 1 ? 'Show Summary' : 'Next Question'}
          </button>
        </div>
      )}
    </div>
  );
}; 